import { useTranslation } from 'react-i18next';
import type { Product, ProductPosition } from '@/api/products';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { formatMoney } from '../format-money';
import { parseDescription } from './product-description';
import { useDirectSaleMethod } from '../useDirectSaleMethod';
import { positionMethodName, positionPrice } from '../direct-sale-methods';

type SizeDimension = NonNullable<Product['dimensions']>[number];

/** SI symbol, the same in every supported language; not a translation. */
const UNIT_CM = 'cm';

/**
 * What a customer wants to know about the product before buying, without
 * leaving the editor. The same fields as the product details of the
 * phone's "more" sheet; with room to spare the facts sit next to the
 * description instead of below it. The catalogue's description is HTML
 * (mostly lists of facts) and is rendered as plain bullets and paragraphs,
 * never injected. Sizes with measurements get a table like the shops'.
 */
export function DirectSaleProductDetailsDialog({
  open,
  onOpenChange,
  product,
  sizes,
  labels,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product;
  /** The sizes the chosen colour offers. */
  sizes: { id: string; name: string }[];
  /**
   * Column names of the size table, translated by the parent. `depth`
   * names the optional third measurement; without it that column stays
   * hidden even when the catalogue sends one.
   */
  labels: { width: string; length: string; depth?: string };
}) {
  const { t, i18n } = useTranslation();
  // Catalogue prices carry no currency of their own; the quote is not
  // needed (and without a design not there) to show them.
  const money = (amount: string | number) =>
    formatMoney(amount, 'EUR', i18n.language);
  // The catalogue keeps one decimal (Decimal(5,1)); showing exactly that
  // keeps "46" and "68,5" aligned in one column.
  const centimetres = new Intl.NumberFormat(i18n.language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  const pricedPositions = (product.positions ?? []).filter(
    (p) => p.printSurcharge != null
  );
  // Prices and names follow the decoration method the customer chose.
  const { effectiveId: methodId } = useDirectSaleMethod(product);
  const positionLabel = (position: ProductPosition) => {
    const method = positionMethodName(position, methodId);
    return method
      ? t('directSale.mobile.decorationOn', {
          method,
          position: position.name,
        })
      : t('directSale.mobile.designOn', { position: position.name });
  };
  const { paragraphs, bullets } = parseDescription(
    product.description || product.shortDescription
  );
  const hasPrices =
    product.recommendedSalesPrice != null || pricedPositions.length > 0;

  // Only the offered sizes matter; a measurement for a size the colour
  // does not come in would mislead. Sizes without one show a dash so the
  // customer sees the gap rather than a shifted row.
  const dimensionsBySize = new Map<string, SizeDimension>(
    (product.dimensions ?? []).map((d) => [d.productSizeId, d])
  );
  const rows = sizes.map((size) => ({
    size,
    dimension: dimensionsBySize.get(size.id),
  }));
  const hasTable = rows.some((row) => row.dimension != null);
  const hasDepth =
    labels.depth != null && rows.some((row) => row.dimension?.lengthCm != null);
  const measure = (value: number | undefined) =>
    value == null ? '–' : centimetres.format(value);

  const withUnit = (label: string) => `${label} (${UNIT_CM})`;

  const heading = 'text-[13px] font-bold text-ink';
  const cell = 'px-3 py-2 text-left';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        closeLabel={t('directSale.design.close')}
        aria-describedby={undefined}
        className="max-h-[85vh] overflow-y-auto min-[1200px]:max-w-3xl"
      >
        <DialogTitle>{t('directSale.mobile.productDetails')}</DialogTitle>

        <div className="grid gap-5 text-sm leading-relaxed text-ink-2 min-[1200px]:grid-cols-2 min-[1200px]:gap-8">
          <div className="flex flex-col gap-3">
            <div>
              <h3 className="font-display text-[17px] font-bold text-ink">
                {product.name}
              </h3>
              {product.oneLineDescription && (
                <p className="mt-0.5 text-ink-3">
                  {product.oneLineDescription}
                </p>
              )}
            </div>
            {paragraphs.map((paragraph, index) => (
              <p key={`${index}-${paragraph}`}>{paragraph}</p>
            ))}
            {bullets.length > 0 && (
              <ul className="list-disc space-y-1 pl-5 text-sm text-ink-2">
                {bullets.map((bullet, index) => (
                  <li key={`${index}-${bullet}`}>{bullet}</li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-col gap-5">
            {product.material && (
              <div>
                <h4 className={heading}>{t('directSale.mobile.material')}</h4>
                <p>{product.material}</p>
              </div>
            )}
            {sizes.length > 0 && !hasTable && (
              <div>
                <h4 className={heading}>{t('directSale.mobile.sizes')}</h4>
                <p>{sizes.map((size) => size.name).join(' · ')}</p>
              </div>
            )}
            {hasTable && (
              <div className="overflow-hidden rounded-card border border-line bg-paper">
                <table role="table" className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-[13px] font-bold text-ink">
                      <th scope="col" className={cell}>
                        {t('directSale.mobile.sizes')}
                      </th>
                      <th scope="col" className={cell}>
                        {withUnit(labels.width)}
                      </th>
                      <th scope="col" className={cell}>
                        {withUnit(labels.length)}
                      </th>
                      {hasDepth && labels.depth && (
                        <th scope="col" className={cell}>
                          {withUnit(labels.depth)}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ size, dimension }) => (
                      <tr key={size.id} className="odd:bg-paper-2">
                        <td className={`${cell} font-semibold text-ink`}>
                          {size.name}
                        </td>
                        <td className={`${cell} tabular-nums`}>
                          {measure(dimension?.widthCm)}
                        </td>
                        <td className={`${cell} tabular-nums`}>
                          {measure(dimension?.heightCm)}
                        </td>
                        {hasDepth && (
                          <td className={`${cell} tabular-nums`}>
                            {measure(dimension?.lengthCm)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {hasPrices && (
              <div>
                <h4 className={heading}>{t('directSale.mobile.prices')}</h4>
                <dl className="mt-1 divide-y divide-line rounded-card border border-line">
                  {product.recommendedSalesPrice != null && (
                    <div className="flex justify-between gap-4 px-3.5 py-2.5 text-ink">
                      <dt>{product.name}</dt>
                      <dd className="font-semibold">
                        {money(product.recommendedSalesPrice)}
                      </dd>
                    </div>
                  )}
                  {pricedPositions.map((position) => (
                    <div
                      key={position.id}
                      className="flex justify-between gap-4 px-3.5 py-2.5"
                    >
                      <dt>{positionLabel(position)}</dt>
                      <dd className="font-semibold text-ink">
                        +{money(positionPrice(position, methodId) ?? 0)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
